import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import 'whatwg-fetch';
// Note: 'whatwg-fetch' is used only to ensure fetch is available in some environments; remove if unnecessary.
// If you prefer not to include this polyfill, remove the import.
// The App Check debug token below will be used for local emulator testing.

// Firebase configuration is environment-driven.
// CRA loads .env, then environment-specific files like .env.development/.env.production.
const fallbackFirebaseConfig = {
  apiKey: 'AIzaSyAB-aZ6GFW1qJ2cyhrGlSecOM7ChlMS3UY',
  authDomain: 'maritaca-call.firebaseapp.com',
  projectId: 'maritaca-call',
  storageBucket: 'maritaca-call.firebasestorage.app',
  messagingSenderId: '855882863165',
  appId: '1:855882863165:web:e63f76d5fd3217d4ff1054',
  measurementId: 'G-Q149LLVGRW'
};

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || fallbackFirebaseConfig.apiKey,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || fallbackFirebaseConfig.authDomain,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || fallbackFirebaseConfig.projectId,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || fallbackFirebaseConfig.storageBucket,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || fallbackFirebaseConfig.messagingSenderId,
  appId: process.env.REACT_APP_FIREBASE_APP_ID || fallbackFirebaseConfig.appId,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || fallbackFirebaseConfig.measurementId
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

let analytics;
if (typeof window !== 'undefined') {
  try {
    analytics = getAnalytics(app);
  } catch (e) {
    // Analytics may fail to initialize in some environments (e.g. tests, SSR)
    // Keep this silent and continue without throwing.
    // eslint-disable-next-line no-console
    console.warn('Firebase analytics not available:', e && e.message ? e.message : e);
  }
}

// Export Firestore and Functions instances for app-wide usage
const db = getFirestore(app);
const functions = getFunctions(app);
const auth = getAuth(app);

const isRunningOnLocalhost = typeof window !== 'undefined'
  && window.location
  && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const emulatorFlag = process.env.REACT_APP_USE_EMULATORS;
const shouldUseEmulators = (typeof emulatorFlag === 'string')
  ? emulatorFlag.toLowerCase() === 'true'
  : isRunningOnLocalhost;

// If the flag is enabled (or we are on localhost without explicit override), connect to emulators.
if (shouldUseEmulators) {
  // Default emulator ports: Firestore 8080, Functions 5001
  try {
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    connectAuthEmulator(auth, 'http://localhost:9099');
    // Initialize App Check in debug mode for local emulators so App Check enforcement
    // does not cause 401 Unauthorized. We set a debug token and initialize App Check
    // with a ReCaptcha provider; the SDK will send the debug token instead of a real
    // attestation when present in localStorage or on the global object.
    try {
      const DEBUG_TOKEN_KEY = 'firebase:appCheck:debugToken';
      const DEFAULT_DEBUG_TOKEN = 'maritaca-debug-token';
      // Place the debug token in both localStorage and on the global to be safe.
      try {
        if (!localStorage.getItem(DEBUG_TOKEN_KEY)) {
          localStorage.setItem(DEBUG_TOKEN_KEY, DEFAULT_DEBUG_TOKEN);
        }
        if (!localStorage.getItem('FIREBASE_APPCHECK_DEBUG_TOKEN')) {
          localStorage.setItem('FIREBASE_APPCHECK_DEBUG_TOKEN', DEFAULT_DEBUG_TOKEN);
        }
        // also set global variable that SDK checks, prefer window in browsers
        try {
          if (typeof window !== 'undefined') {
            window.FIREBASE_APPCHECK_DEBUG_TOKEN = localStorage.getItem(DEBUG_TOKEN_KEY);
          }
        } catch (e) {
          // ignore
        }
      } catch (e) {
        // ignore storage errors
      }
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider('6Lf-placeholder-site-key'), // placeholder; debug token will be used
        isTokenAutoRefreshEnabled: true,
      });
      // Note: some versions of the firebase SDK do not export connectAppCheckEmulator.
      // We rely on the debug token being present (above) so the App Check header is
      // sent with requests. If you run an App Check emulator, you can connect it
      // here when supported by your SDK version.
    } catch (e) {
      console.warn('Failed to initialize App Check debug mode:', e && e.message ? e.message : e);
    }
    // You can also connect auth or other emulators here if you run them locally.
    // e.g. connectAuthEmulator(getAuth(app), 'http://localhost:9099');
    // eslint-disable-next-line no-console
    console.info('Connected Firebase SDK to local emulators (Firestore:8080, Functions:5001, AppCheck:9090)');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to connect to Firebase emulators:', e && e.message ? e.message : e);
  }
}

export { app, analytics, db, functions, auth };
